import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { formatCurrency } from '../../lib/format';
import { wijzigReparatiePrijs, type ReparatieOpdracht } from '../../lib/reparatie';

interface Props {
  opdracht: ReparatieOpdracht;
  onClose: () => void;
  onDone: () => void;
}

/**
 * Prijs aanpassen ná klant-akkoord.
 *
 * De klantprijs staat vooraan, niet de marge: met een klant spreek je een
 * bedrag af (incl. BTW) en de marge is de uitkomst. Casus WB-323684 — de
 * reparateur prijsde per kant, de klant ging akkoord met een bedrag per kant,
 * en de opdracht stond op één kant. Het systeem verwees naar de
 * reparatie-opdracht en daar zat geen knop.
 */
export function ReparatiePrijsDialog({ opdracht, onClose, onDone }: Props) {
  // De reparateur offreert excl. BTW; opgeslagen wordt incl. (SF-formule
  // Verkoop = Inkoop x (1 + Marge), met Verkoop incl. BTW).
  const [inkoop, setInkoop] = useState(
    opdracht.inkoopBedrag != null
      ? String(Math.round((opdracht.inkoopBedrag / 1.21) * 100) / 100).replace('.', ',')
      : ''
  );
  const [klantprijs, setKlantprijs] = useState(
    opdracht.verkoopBedrag != null ? String(opdracht.verkoopBedrag).replace('.', ',') : ''
  );
  const [reden, setReden] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [melding, setMelding] = useState<string | null>(null);

  const inkoopExcl = parseBedrag(inkoop);
  const inkoopNum = inkoopExcl != null ? Math.round(inkoopExcl * 1.21 * 100) / 100 : null;
  const klantNum = parseBedrag(klantprijs);
  const marge = useMemo(() => {
    if (!inkoopNum || !klantNum || inkoopNum <= 0) return null;
    return Math.round((klantNum / inkoopNum - 1) * 100 * 100) / 100;
  }, [inkoopNum, klantNum]);

  const oudeKlantprijs = opdracht.verkoopBedrag ?? null;
  const verschil = klantNum != null && oudeKlantprijs != null ? klantNum - oudeKlantprijs : null;
  const canSubmit = !busy && !!inkoopNum && !!klantNum && inkoopNum > 0 && klantNum > 0 && reden.trim().length >= 5;

  const handleSubmit = async () => {
    if (!canSubmit || inkoopNum == null || klantNum == null) return;
    setBusy(true);
    setError(null);
    try {
      const res = await wijzigReparatiePrijs({
        opdrachtId: opdracht.id,
        inkoopBedrag: inkoopNum,
        klantprijsIncl: klantNum,
        reden: reden.trim(),
      });
      if (!res.success) {
        setError(res.message || 'Prijs aanpassen mislukt');
        setBusy(false);
        return;
      }
      // De betaallink-melding komt uit de server; even laten zien vóór sluiten.
      if (res.message) {
        setMelding(res.message);
        setTimeout(onDone, 1800);
        return;
      }
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Prijs aanpassen mislukt');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Prijs aanpassen</h2>
          <p className="text-xs text-slate-500 mt-1">
            {opdracht.opdrachtNummer}
            {opdracht.reparateurNaam ? ` — ${opdracht.reparateurNaam}` : ''}
            {oudeKlantprijs != null ? ` · nu ${formatCurrency(oudeKlantprijs)} incl. BTW` : ''}
          </p>
        </div>

        {melding ? (
          <div className="px-6 py-6">
            <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
              {melding}
            </p>
          </div>
        ) : (
          <>
            <div className="px-6 py-4 space-y-3">
              <div className="text-xs rounded px-2.5 py-2 border bg-amber-50 border-amber-200 text-amber-900">
                De klant heeft al een bedrag gezien. Pas dit alleen aan als je het nieuwe bedrag
                met de klant hebt afgestemd — het gaat op de factuur en in de betaallink.
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Prijs reparateur <span className="text-slate-400 font-normal">(excl. BTW)</span>
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
                    value={inkoop}
                    onChange={(e) => setInkoop(e.target.value)}
                    disabled={busy}
                    placeholder="157,00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Klant betaalt <span className="text-slate-400 font-normal">(incl. BTW)</span>
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    autoFocus
                    className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
                    value={klantprijs}
                    onChange={(e) => setKlantprijs(e.target.value)}
                    disabled={busy}
                    placeholder="350,00"
                  />
                </div>
              </div>

              {marge != null && inkoopNum != null && (
                <p className="text-xs text-slate-500">
                  Onze kosten incl. BTW {formatCurrency(inkoopNum)} · klant betaalt{' '}
                  {klantNum != null ? formatCurrency(Math.round((klantNum / 1.21) * 100) / 100) : '—'} excl. BTW
                  {' · '}marge wordt {marge.toFixed(2).replace('.', ',')}%
                  {verschil != null && verschil !== 0 && (
                    <>
                      {' · '}
                      <span className={verschil > 0 ? 'text-emerald-700' : 'text-rose-700'}>
                        {verschil > 0 ? '+' : ''}
                        {formatCurrency(verschil)} op het ordertotaal
                      </span>
                    </>
                  )}
                </p>
              )}

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Reden <span className="text-slate-400 font-normal">(verplicht, minimaal 5 tekens)</span>
                </label>
                <textarea
                  rows={2}
                  maxLength={255}
                  className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
                  value={reden}
                  onChange={(e) => setReden(e.target.value)}
                  disabled={busy}
                  placeholder="Bijv. prijs was per kant, klant akkoord met € 175 per kant (mail 31 aug)"
                />
              </div>
              {error && <p className="text-xs text-red-600">{error}</p>}
            </div>
            <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
              >
                Terug
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={clsx(
                  'text-sm font-medium px-3 py-1.5 rounded',
                  !canSubmit
                    ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                )}
              >
                {busy ? 'Bezig…' : 'Zet nieuwe prijs'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** "175,50" en "175.50" leiden beide tot 175.5; leeg of onzin geeft null. */
function parseBedrag(v: string): number | null {
  const n = Number(v.replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : null;
}
